import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { isArchivedAssignment } from './assignmentHistory';

/**
 * Bölge sorumlusu görünürlüğünün ortak kuralları (useAllowedEmployeeIds ve
 * Bölge Müdürleri Raporları):
 *
 *  1. Merkez kuralı (asıl kural): projede bir merkeze atanmış çalışan, o
 *     merkezin ilinden sorumlu bölge müdürüne / il sorumlusuna görünür
 *     ("Ayrıldı" damgalı eski atamalar dahil; bu kural değişmedi).
 *  2. Çalıştığı İl (Employees.workCity): yalnızca hiçbir projede aktif merkez
 *     ataması olmayan çalışanlar için (ayrıldığı merkezler sayılmaz); çalışan
 *     bu ilin sorumlusuna da görünür. İli boş olan çalışan bu kurala hiç girmez.
 */

// Çalışan kaydındaki "Çalıştığı İl" alanı
export const WORK_CITY_FIELD = 'workCity';

/**
 * İl adlarını karşılaştırılabilir yapar: Türkçe küçük harf; şapka ve Türkçe
 * harf farkları yok sayılır ("Hakkâri" = "hakkari", "İzmir" = "izmir").
 */
export const foldCity = (value) =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .trim();

// cityManagerIds değerleri eski kayıtlarda tek id, yenilerde id listesi
const asIdList = (value) => (Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : []));

/**
 * Kullanıcının sorumlu olduğu iller (foldCity ile): bölge müdürü (managerId)
 * bölgenin tüm illeri, il sorumlusu (cityManagerIds) yalnızca kendi ili.
 */
export const getManagedCities = (regions, employeeId) => {
  const cities = new Set();
  if (!employeeId) return cities;
  regions.forEach((region) => {
    if (region.managerId === employeeId) {
      (region.cities || []).forEach((city) => cities.add(foldCity(city)));
    }
    Object.entries(region.cityManagerIds || {}).forEach(([cityKey, managerIds]) => {
      if (asIdList(managerIds).includes(employeeId)) cities.add(foldCity(cityKey));
    });
  });
  cities.delete('');
  return cities;
};

/**
 * Herhangi bir projede aktif bir merkez ataması olan çalışanlar. "Ayrıldı"
 * (unassignedAt) damgalı satırlar sayılmaz: tüm merkezlerinden ayrılmış
 * çalışan Çalıştığı İl'e göre de görünür.
 */
export const getCenterAssignedIds = (projectDocs) => {
  const ids = new Set();
  projectDocs.forEach((projectDoc) => {
    (projectDoc.data().employees || []).forEach((emp) => {
      if (emp.id && emp.centerId && !isArchivedAssignment(emp)) ids.add(emp.id);
    });
  });
  return ids;
};

/**
 * Aktif merkez ataması olmayan ve Çalıştığı İl'i `cities` (foldCity ile) içinde
 * olan çalışanlar, `{ id, ...data }` olarak. Sorgu yalnızca ili dolu kayıtları
 * döndürür; ili boş çalışanlar hiç okunmaz.
 */
export const fetchCityBasedEmployees = async (cities, centerAssignedIds) => {
  if (!cities || cities.size === 0) return [];
  const snapshot = await getDocs(query(collection(db, 'Employees'), where(WORK_CITY_FIELD, '>', '')));
  return snapshot.docs
    .map((employeeDoc) => ({ id: employeeDoc.id, ...employeeDoc.data() }))
    .filter((emp) => !centerAssignedIds.has(emp.id) && cities.has(foldCity(emp[WORK_CITY_FIELD])));
};
